import { toast } from 'react-toastify';
import { Eye, EyeOff } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button, CustomButton } from '../../utils/Button';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError, resetLoading } from '../../features/auth/authSlice';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  fetchCart,
  getCartSessionId,
  transferGuestCartToUser,
} from '../../features/cart/cartSlice';
import { useSendOTP } from '../../hooks/useSendOTP';
import { resendVerifyEmailOTP as resendVerifyEmailAPI } from '../../api/authAPI';

const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [timeLeft, setTimeLeft] = useState(86400);

  const { isAuthenticated, error } = useSelector((state) => state.auth);

  const { mutate: resendVerifyEmailOTP } = useSendOTP(
    resendVerifyEmailAPI,
    () => setTimeLeft(86400)
  );

  const { loading } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(resetLoading());
  }, [dispatch]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [passwordVisible, setPasswordVisible] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      toast.dismiss();
      toast.success('Login successful!', {
        autoClose: 3000,
        className:
          'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
        bodyClassName: 'm-0 p-0',
        closeButton: false,
      });
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    if (error) {
      setFormData((prev) => ({ ...prev, password: '' }));
    }
  }, [error]);

  // Handle input changes
  const handleChange = (e) => {
    if (error) dispatch(clearError());
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearError()), 3000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Handle form submission

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.dismiss();
      toast.error('Please fill in all fields', {
        autoClose: 3000,
        className:
          'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
        bodyClassName: 'm-0 p-0',
        closeButton: false,
      });
      return;
    }

    try {
      // Try logging in
      await dispatch(login(formData)).unwrap();

      // If login success, handle cart
      const cartSessionID = getCartSessionId();
      if (cartSessionID) {
        try {
          await dispatch(transferGuestCartToUser(cartSessionID)).unwrap();
        } catch (error) {}
      }
    } catch (err) {
      if (err?.requires_verification) {
        toast.dismiss();
        toast.warning('Please verify your email to continue.', {
          autoClose: 1000,
          className:
            'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
          bodyClassName: 'm-0 p-0',
          closeButton: false,
        });

        //  Silent OTP send (no toast)
        resendVerifyEmailOTP({ email: err.email, silent: true });

        navigate(`/sign-up/verify-email/${encodeURIComponent(err.email)}`);
      } else {
        // Normal login error
        toast.dismiss();
        toast.error(err?.message || 'Invalid email or password', {
          autoClose: 3000,
          className:
            'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
          bodyClassName: 'm-0 p-0',
          closeButton: false,
        });
      }
    }
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <form
      className=" space-y-4 w-[100%] md:max-w-sm lg:max-w-md lg:flex flex-col justify-start "
      onSubmit={handleSubmit}
    >
      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium sr-only">
          Email address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          disabled={loading}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bg-[var(--yellow-primary)]  disabled:opacity-50"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="password" className="text-sm font-medium sr-only">
          Password
        </label>
        <div className="relative">
          <input
            type={passwordVisible ? 'text' : 'password'}
            id="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            disabled={loading}
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bg-[var(--yellow-primary)]  disabled:opacity-50"
          />
          <button
            type="button"
            onClick={() => setPasswordVisible(!passwordVisible)}
            className="absolute top-3 right-3 text-gray-500 hover:text-bg-[var(--yellow-primary)] "
          >
            {passwordVisible ? <EyeOff /> : <Eye />}
          </button>
        </div>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      <CustomButton
        type="submit"
        text={
          loading ? (
            <div className="flex items-center justify-center">
              <span className="font-duplet mr-1">Logging in...</span>
              <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin inline-block" />
            </div>
          ) : (
            'Log in'
          )
        }
        bgColor="var(--yellow-primary)"
        hoverColor="var(--btn-hover-bg-primary)"
        borderRadius="40px"
        width="100%"
        fontWeight="700"
        textSize="1.125rem"
        disabled={loading}
        className="mt-4 py-3 px-6 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50"
      />

      <Link
        to="/forgot-password"
        className="font-semibold flex justify-center mt-2 hover:underline"
      >
        Forgot Password?
      </Link>
    </form>
  );
};

export default LoginForm;
